import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Stepper, Step, StepLabel, Typography, Paper, Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { StepsAtencionCrisis } from './FormularioAtencionCrisis';
import { StepsAsesoriaCognitiva } from './FormularioAsesoriaCognitiva';
import { StepsAsesoriaComunicativa } from './FormularioAsesoriaComunicativa';
import { StepsAsesoriaPsicologica } from './FormularioAsesoriaPsicologica';
import { StepsUnicaSesion } from './FormularioUnicaSesion';
import { FirmaBienestar } from '../../components/BienestarFirma';
import { generarPdfConFirmas } from '../pdf/generarPdfConFirmas';
import { obtenerValoresFiltros } from '../../../../../global/js/funciones';
import { PARENTESCOS_BIENESTAR, ESTADO_CIVIL, GENERO, ORIENTACION_SEXUAL, INDENTIDAD_GENERO_BIENESTAR, COD_CRISIS, COD_FICHA_UNICA, 
    COD_APRENDER, COD_COMUNICATE, COD_SOCIOEMOCIONAL } from '../../../helper';
import TareasTerminadas from '../../../../general/TareasTerminadas';
import emma_w from '../../../../../global/imagenes/emma_w.png';
import {
    generarEstadoInicial,
    VALIDACIONES_FICHA,
    obtenerDesalineacionesValidacionFichas,
} from '../schemas/fichaSchema';
import { ProteccionDatos } from '../../components/BienestarTextoLegal';
import { FORM_DICTIONARY } from '../schemas/formFields';
import {
    buildPrefillCamposGenerales,
    buildPrefillCamposFichaEspecificos,
} from '../../helpers/solicitudFichaPrefill';
import { mergeDatosGuardadosEnEstado, serializarEstadoFicha } from '../../helpers/fichaSeguimientoProgreso';
import { isFichaSeguimientoCompleta } from '../../helpers/isFichaSeguimientoCompleta';
import {
    fetchBienestarDetalleSeguro,
    fetchDatosUsuarioBienestar,
    fetchDatosPreviosEstudiante,
    fetchProgresoFichaSeguimientoRaw,
    postProgresoFichaSeguimiento,
} from '../../services/bienestarDetalleService';

const SEGUIMIENTOS_CONFIG = {
    [COD_CRISIS]: StepsAtencionCrisis,
    [COD_FICHA_UNICA]: StepsUnicaSesion,
    [COD_APRENDER]: StepsAsesoriaCognitiva,
    [COD_COMUNICATE]: StepsAsesoriaComunicativa,
    [COD_SOCIOEMOCIONAL]: StepsAsesoriaPsicologica,
};
const TIPOS_FICHA_CON_PREFILL_ESPECIFICO = new Set([
    COD_CRISIS,
    COD_FICHA_UNICA,
    COD_APRENDER,
    COD_COMUNICATE,
    COD_SOCIOEMOCIONAL,
]);

const DETALLE_BIENESTAR_CACHE = new Map();

if (process.env.NODE_ENV !== 'production') {
    const desalineaciones = obtenerDesalineacionesValidacionFichas();
    if (desalineaciones.length) {
        console.error('[Bienestar fichas] Keys de validación desalineadas', desalineaciones);
    }
}

const tieneValor = (v) => v !== undefined && v !== null && String(v).trim() !== '';

const mezclarConPrevios = (deLaSolicitud = {}, previos = {}) => {
    const salida = { ...deLaSolicitud };
    Object.keys(previos).forEach((campo) => {
        if (!tieneValor(salida[campo]) && tieneValor(previos[campo])) {
            salida[campo] = previos[campo];
        }
    });
    return salida;
};


export const FormularioFichaSeguimiento = ({ 
    tipoServicio, 
    fichaSeleccionada, 
    data, 
    onChange, 
    pintarSelect,
    opciones,
    onError,
    activeStep = 0,
    ProteccionDatosComponent,
    texto_proteccion_datos,
    firmaRefEntrevistado,
    firmaRefResponsable,
    solicitud,
    profesionales_opciones = [],
    formSteps,
    onChangeSesion,
    onBatchChange,
    readOnly = false,
}) => {
    const theme = useTheme();
    const stepperVertical = useMediaQuery(theme.breakpoints.down("sm"));
    const localFirmaRef = useRef(null);
    const localFirmaRefResponsable = useRef(null);
    const refFirmaEntrevistado = firmaRefEntrevistado || localFirmaRef;
    const refFirmaResponsable = firmaRefResponsable || localFirmaRefResponsable;
    const limpiarFirmaGenerico = (ref) => {
            if (ref && ref.current) {
                ref.current.clear();
            }
        };

    const steps = [
        { label: 'Información Legal', type: 'legal' }, 
        { label: 'Información General', type: 'general' }, 
        ...formSteps.map(step => ({ ...step, type: 'formulario' })),
        { label: 'Cierre y Firma', type: 'firma' }
    ];
    const [datosPrevios, setDatosPrevios] = useState({});

    useEffect(() => {
        let vigente = true;
        if (!solicitud?.id) {
            setDatosPrevios({});
            return undefined;
        }
        fetchDatosPreviosEstudiante(solicitud.id).then((previos) => {
            if (vigente) setDatosPrevios(previos || {});
        });
        return () => { vigente = false; };
    }, [solicitud?.id]);

    const prefillCamposEspecificos = useMemo(() => {
        const tipoFicha = (
            tipoServicio?.codigo === COD_SOCIOEMOCIONAL &&
            fichaSeleccionada?.secundario?.codigo === COD_FICHA_UNICA
        )
            ? COD_FICHA_UNICA
            : tipoServicio?.codigo;

        if (!TIPOS_FICHA_CON_PREFILL_ESPECIFICO.has(tipoFicha)) {
            return {};
        }
        return mezclarConPrevios(buildPrefillCamposFichaEspecificos(solicitud, tipoFicha), datosPrevios);
    }, [solicitud, tipoServicio?.codigo, fichaSeleccionada?.secundario?.codigo, datosPrevios]);
    const huellaPrefillEspecificoRef = useRef('');

    useEffect(() => {
        if (!solicitud || !prefillCamposEspecificos || Object.keys(prefillCamposEspecificos).length === 0) {
            return;
        }

        const huellaSolicitud = [
            solicitud?.id || '',
            solicitud?.usuario_registro?.id || '',
            tipoServicio?.codigo || '',
            fichaSeleccionada?.secundario?.codigo || '',
            Object.keys(datosPrevios).length,
        ].join('|');

        if (huellaPrefillEspecificoRef.current === huellaSolicitud) return;
        huellaPrefillEspecificoRef.current = huellaSolicitud;

        const batch = {};
        Object.entries(prefillCamposEspecificos).forEach(([campo, valor]) => {
            if (valor !== undefined && valor !== null && String(valor).trim() !== '' && !data?.[campo]?.valor) {
                batch[campo] = valor;
            }
        });

        if (Object.keys(batch).length === 0) return;
        if (typeof onBatchChange === 'function') {
            onBatchChange(batch);
        } else {
            Object.entries(batch).forEach(([name, value]) => {
                onChange({ target: { name, value } });
            });
        }
    }, [solicitud, prefillCamposEspecificos, data, onBatchChange, onChange, tipoServicio?.codigo, fichaSeleccionada?.secundario?.codigo]);

    const getStepContent = (stepIndex) => {
        const step = steps[stepIndex];
        switch (step.type) {

            case 'legal':
                return (
                    <>
                        <Typography variant="body2" style={{ marginBottom: 16 }}>
                            Antes de continuar, debes revisar y aceptar los siguientes términos:
                        </Typography>
                        
                        {texto_proteccion_datos && (
                            <div style={{ marginTop: 12 }}>
                                <ProteccionDatos
                                    texto={texto_proteccion_datos}
                                    value={data?.acepta_proteccion_datos?.valor}
                                    onChange={(checked) => {
                                        // Convertir el valor booleano en un evento para handleChange
                                        onChange({
                                            target: {
                                                name: 'acepta_proteccion_datos',
                                                value: checked,
                                                type: 'checkbox',
                                                checked: checked
                                            }
                                        });
                                    }}
                                    name="acepta_proteccion_datos"
                                />
                            </div>
                        )}
                    </>
                    );

            case 'general':
                return (
                    <FormularioGeneral
                        data={data}
                        onChange={onChange}
                        solicitud={solicitud}
                        onBatchChange={onBatchChange}
                        datosPrevios={datosPrevios}
                    />
                );

            case 'formulario':
                return step.render({
                    data,
                    onChange,
                    onChangeSesion,
                    pintarSelect,
                    opciones,
                    onError,
                    solicitud,
                    onBatchChange,
                    readOnly,
                });

            case 'firma':
                return (
                    <FirmaBienestar 
                        firmaRefEntrevistado={refFirmaEntrevistado} 
                        firmaRefResponsable={refFirmaResponsable} 
                        limpiarFirma={limpiarFirmaGenerico}
                        requiereDobleFirma={false}
                        profesionalesOpciones={profesionales_opciones}
                        data={data}
                        onChange={onChange}
                        onError={onError}
                        readOnly={readOnly}
                    />
                );

            default:
                return <Typography>Paso no encontrado</Typography>;
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel={!stepperVertical}
                    orientation={stepperVertical ? "vertical" : "horizontal"}
                    style={{
                        background: "transparent",
                        padding: stepperVertical ? "8px 0 8px 4px" : "4px 0",
                    }}
                >
                    {steps.map((step, index) => (
                        <Step key={index}>
                            <StepLabel
                                style={stepperVertical ? { alignItems: "flex-start" } : undefined}
                            >
                                <Typography
                                    variant="caption"
                                    component="span"
                                    style={{
                                        display: "block",
                                        textAlign: stepperVertical ? "left" : "center",
                                        maxWidth: stepperVertical ? "none" : 140,
                                        margin: stepperVertical ? 0 : "0 auto",
                                        lineHeight: 1.25,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {step.label}
                                </Typography>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Grid>
            <Grid item xs={12} style={{ minHeight: '300px'}}>
                {readOnly ? (
                    <fieldset
                        disabled
                        style={{
                            border: 'none',
                            margin: 0,
                            padding: 0,
                            minWidth: 0,
                        }}
                    >
                        {getStepContent(activeStep)}
                    </fieldset>
                ) : (
                    <div>{getStepContent(activeStep)}</div>
                )}
            </Grid>
        </Grid>
    );
};


function FormularioGeneral({ data, onChange, solicitud, onBatchChange, datosPrevios = {} }) {
    const solicitudMap = useMemo(
        () => mezclarConPrevios(buildPrefillCamposGenerales(solicitud), datosPrevios),
        [solicitud, datosPrevios]
    );

    const safeValue = (field) => {
        const valor = data?.[field]?.valor;
        if (valor !== undefined && valor !== null && String(valor).trim() !== '') {
            return valor;
        }
        return solicitudMap[field] ?? '';
    };
    const safeError = (field) => (data?.[field]?.error || '');
    const [parentescos, setParentescos] = useState([]);
    const [estadoCivil, setEstadoCivil] = useState([]);
    const [generos, setGeneros] = useState([]);
    const [identidadesGenero, setIdentidadesGenero] = useState([]);
    const [orientacionSexual, setOrientacionSexual] = useState([]);
    const [programasOpciones, setProgramasOpciones] = useState([]);

    const normalizarProgramas = (programasRaw = []) =>
        (Array.isArray(programasRaw) ? programasRaw : [])
            .map((prog) => {
                const value = prog?.id_relacion
                    ?? prog?.relacion?.id_relacion
                    ?? prog?.relacion?.id_programa
                    ?? prog?.relacion?.id
                    ?? prog?.id_programa
                    ?? prog?.id
                    ?? null;
                const label = prog?.nombre_relacion
                    ?? prog?.relacion?.nombre_relacion
                    ?? prog?.relacion?.nombre
                    ?? prog?.nombre_programa
                    ?? prog?.nombre
                    ?? '';
                if (value === null || String(label).trim() === '') return null;
                return { value, label: String(label).trim() };
            })
            .filter(Boolean);

    const normalizarValorSegunOpciones = (valor, opciones = []) => {
        if (valor === undefined || valor === null || String(valor).trim() === '') return '';
        const texto = String(valor).trim();
        const matchLabel = opciones.find((op) => String(op.label).trim().toLowerCase() === texto.toLowerCase());
        if (matchLabel) return matchLabel.label;

        const matchId = opciones.find((op) => String(op.value) === texto || String(op.value).trim() === texto);
        if (matchId) return matchId.label;

        return texto;
    };

    const valorCoincideConOpciones = (valor, opciones = []) => {
        if (valor === undefined || valor === null || String(valor).trim() === '') return false;
        const t = String(valor).trim();
        return opciones.some(
            (op) =>
                String(op.label).trim() === t ||
                String(op.label).trim().toLowerCase() === t.toLowerCase() ||
                String(op.value) === t
        );
    };

    const calcularEdad = (fecha) => {
        if (!fecha) return "";
        const hoy = new Date();
        const nacimiento = new Date(fecha);
        let edad = hoy.getFullYear() - nacimiento.getFullYear();
        const mes = hoy.getMonth() - nacimiento.getMonth();
        if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
            edad--;
        }
        return edad;
    };

    const cargarCatalogo = async (tipo, setState) => {
        try {
            const data = await obtenerValoresFiltros([
                { llave: 'generica', valor: tipo },
                { llave: 'estado', valor: 1 }
            ]);
            const opciones = (Array.isArray(data) ? data : []).map(item => ({
                value: item.id || item.codigo || item.valor,
                label: item.nombre || item.valor || item.label || ""
            }));
            setState(opciones);
        } catch (error) {
            console.error(`Error cargando ${tipo}:`, error);
        }
    };

    useEffect(() => {
        const cargarTodo = async () => {
            await Promise.all([
                cargarCatalogo(PARENTESCOS_BIENESTAR, setParentescos),
                cargarCatalogo(ESTADO_CIVIL, setEstadoCivil),
                cargarCatalogo(GENERO, setGeneros),
                cargarCatalogo(INDENTIDAD_GENERO_BIENESTAR, setIdentidadesGenero),
                cargarCatalogo(ORIENTACION_SEXUAL, setOrientacionSexual),
            ]);

            const delEstudiante = normalizarProgramas(
                solicitud?.programas || solicitud?.usuario_registro?.programas || []
            );

            const deLaSolicitud = normalizarProgramas(
                [solicitud?.programa].filter((prog) => prog && typeof prog === 'object')
            );

            const opciones = [
                ...deLaSolicitud,
                ...delEstudiante.filter(
                    (op) => !deLaSolicitud.some((ds) => String(ds.value) === String(op.value))
                ),
            ];

            if (opciones.length > 0) {
                setProgramasOpciones(opciones);
                return;
            }


            const resp = await fetchDatosUsuarioBienestar();
            setProgramasOpciones(resp?.programas ? normalizarProgramas(resp.programas) : []);
        };
        cargarTodo();
    }, [solicitud]);

    useEffect(() => {
        if (!solicitud) return;

        const camposAInicializar = [
            'programa',
            'semestre',
            'fecha_nacimiento',
            'estado_civil',
            'sexo_biologico',
            'identidad_genero',
            'orientacion_sexual',
            'direccion_actual',
            'barrio',
            'lugar_procedencia',
            'departamento',
            'municipio',
            'correo_electronico',
            'celular',
            'contacto_emergencia',
            'celular_contacto_emergencia',
            'parentesco',
        ];

        const batch = {};
        camposAInicializar.forEach((campo) => {
            let valor = solicitudMap[campo];
            if (campo === 'programa') {
                valor = normalizarValorSegunOpciones(valor, programasOpciones);
            } else if (campo === 'estado_civil') {
                valor = normalizarValorSegunOpciones(valor, estadoCivil);
            } else if (campo === 'sexo_biologico') {
                valor = normalizarValorSegunOpciones(valor, generos);
            } else if (campo === 'identidad_genero') {
                valor = normalizarValorSegunOpciones(valor, identidadesGenero);
            } else if (campo === 'orientacion_sexual') {
                valor = normalizarValorSegunOpciones(valor, orientacionSexual);
            } else if (campo === 'parentesco') {
                if (!parentescos || parentescos.length === 0) {
                    return;
                }
                valor = normalizarValorSegunOpciones(valor, parentescos);
            }

            if (valor === undefined || valor === null || String(valor).trim() === '') {
                return;
            }

            if (campo === 'parentesco' && data?.[campo]?.valor) {
                if (valorCoincideConOpciones(data[campo].valor, parentescos)) {
                    return;
                }
            } else if (data?.[campo]?.valor) {
                return;
            }

            batch[campo] = valor;
        });

        if (Object.keys(batch).length === 0) {
            return;
        }
        if (typeof onBatchChange === 'function') {
            onBatchChange(batch);
        } else {
            Object.entries(batch).forEach(([name, value]) => {
                onChange({ target: { name, value } });
            });
        }
        // No incluir `data` en dependencias: evita re-prefill en cada tecla y respeta campos vaciados por el usuario.
    }, [solicitud, solicitudMap, estadoCivil, generos, identidadesGenero, orientacionSexual, parentescos, programasOpciones, onBatchChange, onChange]);

    const edad = calcularEdad(safeValue("fecha_nacimiento"));
    const nombreCompleto = `${solicitud?.usuario_registro?.primer_nombre || ''} ${solicitud?.usuario_registro?.segundo_nombre || ''} ${solicitud?.usuario_registro?.primer_apellido || ''} ${solicitud?.usuario_registro?.segundo_apellido || ''}`.trim();
    const campos_generales = FORM_DICTIONARY.generales;

    return (
        <Grid container spacing={2}>

            <Grid item xs={12} md={4}>
                <TextField fullWidth 
                label={campos_generales.nombre.label} 
                name={campos_generales.nombre.name} 
                value={nombreCompleto} inputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField fullWidth 
                label={campos_generales.documento.label} 
                name={campos_generales.documento.name} 
                value={solicitud?.usuario_registro?.identificacion || ''} inputProps={{ readOnly: true }} />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    select 
                    label={campos_generales.programa.label}
                    name={campos_generales.programa.name}
                    value={safeValue(campos_generales.programa.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.programa.name)}
                    helperText={safeError(campos_generales.programa.name)}
                >
                    {programasOpciones.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.label}>
                            {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
                <TextField 
                    fullWidth 
                    label={campos_generales.semestre.label}
                    name={campos_generales.semestre.name}
                    value={safeValue(campos_generales.semestre.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.semestre.name)}
                    helperText={safeError(campos_generales.semestre.name)}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <TextField
                fullWidth
                type="date"
                label={campos_generales.fecha_nacimiento.label}
                name={campos_generales.fecha_nacimiento.name}
                value={safeValue(campos_generales.fecha_nacimiento.name)}
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
                required
                error={!!safeError(campos_generales.fecha_nacimiento.name)}
                helperText={safeError(campos_generales.fecha_nacimiento.name)}
                />
            </Grid>

            <Grid item xs={12} md={1}>
                <TextField
                    label="Edad"
                    value={edad}
                    disabled
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <TextField 
                    fullWidth 
                    select 
                    label={campos_generales.estado_civil.label}
                    name={campos_generales.estado_civil.name}
                    value={safeValue(campos_generales.estado_civil.name)} 
                    onChange={onChange}
                    error={!!safeError(campos_generales.estado_civil.name)}
                    helperText={safeError(campos_generales.estado_civil.name)}
                    required
                >
                    {estadoCivil.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.label}>
                            {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
                <TextField 
                    fullWidth 
                    select 
                    label={campos_generales.sexo_biologico.label}
                    name={campos_generales.sexo_biologico.name}
                    value={safeValue(campos_generales.sexo_biologico.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.sexo_biologico.name)}
                    helperText={safeError(campos_generales.sexo_biologico.name)}
                >
                    {generos.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.label}>
                            {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    select 
                    label={campos_generales.identidad_genero.label} 
                    name={campos_generales.identidad_genero.name}
                    value={safeValue(campos_generales.identidad_genero.name)}
                    onChange={onChange}
                    error={!!safeError(campos_generales.identidad_genero.name)}
                    helperText={safeError(campos_generales.identidad_genero.name)}
                    required
                >
                    {identidadesGenero.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.label}>
                            {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    select 
                    label={campos_generales.orientacion_sexual.label}
                    name={campos_generales.orientacion_sexual.name}
                    value={safeValue(campos_generales.orientacion_sexual.name)}
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.orientacion_sexual.name)}
                    helperText={safeError(campos_generales.orientacion_sexual.name)}
                >
                    {orientacionSexual.map((opcion) => (
                        <MenuItem key={opcion.value} value={opcion.label}>
                            {opcion.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    label={campos_generales.direccion_actual.label}
                    name={campos_generales.direccion_actual.name}
                    value={safeValue(campos_generales.direccion_actual.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.direccion_actual.name)}
                    helperText={safeError(campos_generales.direccion_actual.name)}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <TextField 
                    fullWidth 
                    label={campos_generales.barrio.label} 
                    name={campos_generales.barrio.name}
                    value={safeValue(campos_generales.barrio.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.barrio.name)}
                    helperText={safeError(campos_generales.barrio.name)}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <TextField 
                    fullWidth 
                    label={campos_generales.lugar_procedencia.label}
                    name={campos_generales.lugar_procedencia.name}
                    value={safeValue(campos_generales.lugar_procedencia.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.lugar_procedencia.name)}
                    helperText={safeError(campos_generales.lugar_procedencia.name)}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <TextField 
                    fullWidth 
                    label={campos_generales.departamento.label}
                    name={campos_generales.departamento.name}
                    value={safeValue(campos_generales.departamento.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.departamento.name)}
                    helperText={safeError(campos_generales.departamento.name)}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <TextField 
                    fullWidth 
                    label={campos_generales.municipio.label}
                    name={campos_generales.municipio.name}
                    value={safeValue(campos_generales.municipio.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.municipio.name)}
                    helperText={safeError(campos_generales.municipio.name)}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField fullWidth label={campos_generales.correo_electronico.label} name={campos_generales.correo_electronico.name} value={safeValue(campos_generales.correo_electronico.name)} onChange={onChange} />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField fullWidth label={campos_generales.celular.label} name={campos_generales.celular.name} value={safeValue(campos_generales.celular.name)} onChange={onChange} />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    label={campos_generales.contacto_emergencia.label} 
                    name={campos_generales.contacto_emergencia.name} 
                    value={safeValue(campos_generales.contacto_emergencia.name)} 
                    onChange={onChange}
                    error={!!safeError(campos_generales.contacto_emergencia.name)}
                    helperText={safeError(campos_generales.contacto_emergencia.name)}
                    required
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    label={campos_generales.celular_contacto_emergencia.label}
                    name={campos_generales.celular_contacto_emergencia.name} 
                    value={safeValue(campos_generales.celular_contacto_emergencia.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.celular_contacto_emergencia.name)}
                    helperText={safeError(campos_generales.celular_contacto_emergencia.name)}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    select 
                    label={campos_generales.parentesco.label} 
                    name={campos_generales.parentesco.name} 
                    value={safeValue(campos_generales.parentesco.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.parentesco.name)}
                    helperText={safeError(campos_generales.parentesco.name)}
                >
                    {parentescos.map(op => (
                        <MenuItem key={op.value} value={op.label}>
                            {op.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} md={12}></Grid>
        </Grid>
    );
}


export function FormularioFichaSeguimientoConAcciones(props) {
    const theme = useTheme();
    const accionesApiladas = useMediaQuery(theme.breakpoints.down("sm"));
    const [activeStep, setActiveStep] = useState(0);
    const [cargando, setCargando] = useState(false);
    const [solicitudEnriquecida, setSolicitudEnriquecida] = useState(props.solicitud || null);
    const tituloFicha = props.fichaSeleccionada?.secundario?.nombre || "Ficha de Seguimiento";

    const normalizarDetalleBienestar = (resp) => {
        if (Array.isArray(resp)) {
            return resp[0] || null;
        }
        return resp || null;
    };

    const obtenerDetalleBienestar = async (id) => {
        if (!id) return null;
        if (DETALLE_BIENESTAR_CACHE.has(id)) {
            return DETALLE_BIENESTAR_CACHE.get(id);
        }
        const resp = await fetchBienestarDetalleSeguro(id);
        const detalleNormalizado = resp ? normalizarDetalleBienestar(resp) : null;
        if (detalleNormalizado) {
            DETALLE_BIENESTAR_CACHE.set(id, detalleNormalizado);
        }
        return detalleNormalizado;
    };

    useEffect(() => {
        let activo = true;

        const enriquecerSolicitud = async () => {
            const base = props.solicitud || {};
            setSolicitudEnriquecida(base);

            if (!props.bienestarId) {
                return;
            }

            const detalleBienestar = await obtenerDetalleBienestar(props.bienestarId);

            if (!activo) return;

            const usuarioBase = base?.usuario_registro || {};
            const usuarioDetalle = detalleBienestar?.usuario_registro || {};
            const usuarioFusionado = {
                ...(usuarioDetalle || {}),
                ...(usuarioBase || {}),
            };

            const solicitudFusionada = {
                ...(base || {}),
                ...(detalleBienestar || {}),
                usuario_registro: usuarioFusionado,
                fecha_nacimiento:
                    base?.fecha_nacimiento ||
                    detalleBienestar?.fecha_nacimiento ||
                    base?.fecha_nac ||
                    detalleBienestar?.fecha_nac ||
                    '',
                fecha_nac:
                    base?.fecha_nac ||
                    detalleBienestar?.fecha_nac ||
                    base?.fecha_nacimiento ||
                    detalleBienestar?.fecha_nacimiento ||
                    '',
                orientacion_sexual:
                    base?.orientacion_sexual ||
                    detalleBienestar?.orientacion_sexual ||
                    base?.orientacion ||
                    detalleBienestar?.orientacion ||
                    '',
                orientacion:
                    base?.orientacion ||
                    detalleBienestar?.orientacion ||
                    base?.orientacion_sexual ||
                    detalleBienestar?.orientacion_sexual ||
                    '',
            };

            setSolicitudEnriquecida(solicitudFusionada);
        };

        enriquecerSolicitud();

        return () => {
            activo = false;
        };
    }, [props.solicitud, props.bienestarId]);

    const codigoFichaSecundario = useMemo(() => {
        const secundario = props.fichaSeleccionada?.secundario || {};
        const candidatos = [secundario?.codigo, secundario?.valorc, secundario?.valor];

        for (const candidato of candidatos) {
            if (typeof candidato === 'string' || typeof candidato === 'number') {
                const valor = String(candidato).trim();
                const invalido = !valor || ['null', 'undefined', 'nan'].includes(valor.toLowerCase());
                if (!invalido) return valor;
            }
        }

        return '';
    }, [
        props.fichaSeleccionada?.secundario?.codigo,
        props.fichaSeleccionada?.secundario?.valorc,
        props.fichaSeleccionada?.secundario?.valor,
    ]);
    const tipoFicha = useMemo(() => {
        if (
            props.tipoServicio?.codigo === COD_SOCIOEMOCIONAL &&
            props.fichaSeleccionada?.secundario?.codigo === COD_FICHA_UNICA
        ) {
            return COD_FICHA_UNICA;
        }
        return props.tipoServicio?.codigo;
    }, [props.tipoServicio?.codigo, props.fichaSeleccionada?.secundario?.codigo]);

    const [formState, setFormState] = useState(() => generarEstadoInicial(
        props.tipoServicio?.codigo === COD_SOCIOEMOCIONAL &&
            props.fichaSeleccionada?.secundario?.codigo === COD_FICHA_UNICA
            ? COD_FICHA_UNICA
            : props.tipoServicio?.codigo
    ));
    const [estadoProgreso, setEstadoProgreso] = useState('in_progress');
    const [progresoListo, setProgresoListo] = useState(true);
    const [confirmDescargaBorradorOpen, setConfirmDescargaBorradorOpen] = useState(false);
    const [guardandoProgreso, setGuardandoProgreso] = useState(false);

    useEffect(() => {
        let activo = true;

        if (!props.bienestarId) {
            const base = generarEstadoInicial(tipoFicha);
            setFormState(base);
            setActiveStep(0);
            setEstadoProgreso('in_progress');
            setProgresoListo(true);
            return;
        }
        setProgresoListo(false);

        const cargarProgreso = async () => {
            const { ok, resp } = await fetchProgresoFichaSeguimientoRaw({
                bienestarId: props.bienestarId,
                tipoServicioCodigo: tipoFicha,
                codigoFichaSecundario,
            });
            if (!activo) return;

            const base = generarEstadoInicial(tipoFicha);
            if (ok && resp?.existe && resp.datos && typeof resp.datos === 'object') {
                setEstadoProgreso(resp.estado || 'in_progress');
                const paso = Number.isInteger(resp.paso_activo) ? resp.paso_activo : parseInt(resp.paso_activo, 10);
                setActiveStep(Number.isFinite(paso) ? paso : 0);
                setFormState(mergeDatosGuardadosEnEstado(base, resp.datos));
            } else {
                setEstadoProgreso('in_progress');
                setActiveStep(0);
                setFormState(base);
            }
            setProgresoListo(true);
        };

        cargarProgreso();

        return () => {
            activo = false;
        };
    }, [props.bienestarId, tipoFicha, codigoFichaSecundario]);

    const readOnly = estadoProgreso === 'closed';
    const formSteps = useMemo(() => SEGUIMIENTOS_CONFIG[tipoFicha] || [], [tipoFicha]);

    const steps = useMemo(() => [
        { label: 'Información Legal', type: 'legal' },
        { label: 'Información General', type: 'general' },
        ...formSteps.map(step => ({ ...step, type: 'formulario' })),
        { label: 'Cierre y Firma', type: 'firma' },
    ], [formSteps]);
    const isLastStep = activeStep === steps.length - 1;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const valorFinal = type === 'checkbox' ? checked : value;
        
        setFormState(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                valor: valorFinal,
                error: ""
            }
        }));
    };

    const handleBatchChange = useCallback((fieldMap) => {
        if (!fieldMap || typeof fieldMap !== 'object') return;
        setFormState((prev) => {
            const next = { ...prev };
            Object.entries(fieldMap).forEach(([name, value]) => {
                const current = next[name] || { valor: '', error: '' };
                next[name] = {
                    ...current,
                    valor: value,
                    error: '',
                };
            });
            return next;
        });
    }, []);

    const handleChangeSesion = (nuevasSesiones) => {
    setFormState(prev => ({
        ...prev,
        sesiones: nuevasSesiones
    }));
    };
    
    const firmaRef = useRef(null);
    const firmaRefResponsable = useRef(null);
    const guardadoEnCursoRef = useRef(false);
    const ultimoGuardadoTsRef = useRef(0);
    const envioDocumentoEnCursoRef = useRef(false);

    // ============================================================================
    // FUNCIONES AUXILIARES DE VALIDACIÓN
    // ============================================================================

    const obtenerValorCampo = (campo) => {
        const valorCampo = formState?.[campo];
        if (
            valorCampo &&
            typeof valorCampo === 'object' &&
            Object.prototype.hasOwnProperty.call(valorCampo, 'valor')
        ) {
            return valorCampo.valor;
        }
        return valorCampo;
    };

    const esVacio = (valor) => valor === undefined || valor === null || String(valor).trim() === '';

    const mostrarErrorValidacion = (titulo) => {
        if (props.actualizarMensaje) {
            props.actualizarMensaje({
                titulo,
                tipo: 'warning',
                mostrar: true,
                tiempo: 3500,
            });
        }
    };

    const mensajeCamposFaltantes = (campos = [], fallback = 'Completa los campos obligatorios para continuar.') => {
        const labels = [...new Set(campos.map((campo) => campo.label).filter(Boolean))];
        if (!labels.length) return fallback;
        const visibles = labels.slice(0, 4);
        const resto = labels.length - visibles.length;
        const extra = resto > 0 ? ` y ${resto} más` : '';
        return `Completa los campos obligatorios: ${visibles.join(', ')}${extra}.`;
    };

    const setFieldError = (nombre, error = "") => {
        setFormState(prevState => {
            const currentField = prevState[nombre] || { valor: "", error: "" };
            return {
                ...prevState,
                [nombre]: {
                    ...currentField,
                    error
                }
            };
        });
    };

    // ============================================================================
    // VALIDACIÓN POR PASO
    // ============================================================================

    const validarPasoLegal = () => {
        const muestraProteccionDatos = Boolean(props.texto_proteccion_datos);
        const valorAcepta = obtenerValorCampo('acepta_proteccion_datos');
        if (muestraProteccionDatos && !valorAcepta) {
            mostrarErrorValidacion('Debes aceptar la protección de datos para continuar.');
            return false;
        } 
        return true;
    };

    const validarPasoGeneral = () => {
        const configPaso = VALIDACIONES_FICHA?.general;
        
        if (!configPaso?.required) return true;

        let tieneError = false;
        const camposFaltantes = [];
        configPaso.required.forEach(({ key, label }) => {
            const valor = obtenerValorCampo(key);
            
            if (esVacio(obtenerValorCampo(key))) {
                setFieldError(key, `El campo ${label} es obligatorio.`);
                tieneError = true;
                camposFaltantes.push({ key, label });
            } else {
                setFieldError(key, '');
            }
        });

        if (tieneError) {
            mostrarErrorValidacion(mensajeCamposFaltantes(camposFaltantes, 'Completa los campos generales obligatorios para continuar.'));
            return false;
        }

        return true;
    };

    const validarPasoFormulario = (stepNumber) => {

        const configPaso = VALIDACIONES_FICHA?.[tipoFicha]?.[stepNumber];

        if (!configPaso) {
            return true;
        }

        const requiredFields = Array.isArray(configPaso.required) ? configPaso.required : [];
        const conditionalFields = Array.isArray(configPaso.conditionalRequired) ? configPaso.conditionalRequired : [];
        const customValidations = configPaso?.customValidation
            ? (Array.isArray(configPaso.customValidation) ? configPaso.customValidation : [configPaso.customValidation])
            : [];

        if (requiredFields.length === 0 && conditionalFields.length === 0 && customValidations.length === 0) {
            return true;
        }

        let tieneError = false;
        let mensajeErrorCustom = '';
        const camposFaltantes = [];

        requiredFields.forEach(({ key, label }) => {
            const valor = obtenerValorCampo(key);
            if (esVacio(valor)) {
                setFieldError(key, `El campo ${label} es obligatorio.`);
                tieneError = true;
                camposFaltantes.push({ tipo: 'required', key, label, valorActual: valor });
            } else {
                setFieldError(key, '');
            }
        });

        conditionalFields.forEach(({ key, label, condition }) => {
            const aplicaCondicion = condition(formState);
            const valor = obtenerValorCampo(key);
            if (aplicaCondicion && esVacio(valor)) {
                setFieldError(key, `El campo ${label} es obligatorio.`);
                tieneError = true;
                camposFaltantes.push({ tipo: 'conditionalRequired', key, label, valorActual: valor });
            } else {
                setFieldError(key, '');
            }
        });

        customValidations.forEach((validacion) => {
            if (validacion === 'sesiones') {
                const sesiones = formState.sesiones || [];
                if (sesiones.length === 0) {
                    tieneError = true;
                    if (!mensajeErrorCustom) mensajeErrorCustom = 'No hay sesiones configuradas.';
                    return;
                }

                const hayAlgunCheckMarcado = sesiones.some(s =>
                    s.asistencia || s.encuesta || s.reporte || s.nivel_riesgo || s.consentimiento
                );

                if (!hayAlgunCheckMarcado) {
                    tieneError = true;
                    if (!mensajeErrorCustom) mensajeErrorCustom = 'Debes marcar al menos un check en las sesiones para continuar.';
                }
            }

            if (validacion === 'habilidades_comunicativas') {
                const camposHabilidades = ['habcom_leer', 'habcom_escribir', 'habcom_escuchar', 'habcom_hablar'];
                const hayHabilidadMarcada = camposHabilidades.some(campo => !!obtenerValorCampo(campo));

                if (!hayHabilidadMarcada) {
                    setFieldError('habilidades_comunicativas', 'Debes marcar al menos una habilidad comunicativa.');
                    tieneError = true;
                    if (!mensajeErrorCustom) mensajeErrorCustom = 'Debes marcar al menos una habilidad comunicativa para continuar.';
                    camposFaltantes.push({
                        tipo: 'customValidation',
                        key: 'habilidades_comunicativas',
                        label: 'Habilidades comunicativas',
                        detalle: camposHabilidades.reduce((acc, campo) => {
                            acc[campo] = obtenerValorCampo(campo);
                            return acc;
                        }, {})
                    });
                } else {
                    setFieldError('habilidades_comunicativas', '');
                }
            }

            if (validacion === 'habitos_estudio') {
                const camposHabitos = ['hab_Inspeccionar', 'hab_Preguntar', 'hab_Leer', 'hab_Escribir', 'hab_Repasar', 'hab_Otra'];
                const hayHabitoMarcado = camposHabitos.some(campo => !!obtenerValorCampo(campo));

                if (!hayHabitoMarcado) {
                    setFieldError('habitos_estudio', 'Debes marcar al menos un hábito de estudio.');
                    tieneError = true;
                    if (!mensajeErrorCustom) mensajeErrorCustom = 'Debes marcar al menos un hábito de estudio para continuar.';
                } else {
                    setFieldError('habitos_estudio', '');
                }
            }
        });

        if (tieneError) {
            mostrarErrorValidacion(
                mensajeErrorCustom || mensajeCamposFaltantes(camposFaltantes)
            );
            return false;
        }

        return true;
    };

    const validarPasoFirma = () => {
        // Validar responsable_atencion y tp_responsable
        let tieneErrorCampos = false;

        if (esVacio(obtenerValorCampo('responsable_atencion'))) {
            setFieldError('responsable_atencion', 'Selecciona el responsable de la atención.');
            tieneErrorCampos = true;
        } else {
            setFieldError('responsable_atencion', '');
        }
        
        if (esVacio(obtenerValorCampo('tp_responsable'))) {
            setFieldError('tp_responsable', 'El T.P. del responsable es obligatorio.');
            tieneErrorCampos = true;
        } else {
            setFieldError('tp_responsable', '');
        }

        if (tieneErrorCampos) {
            mostrarErrorValidacion('Debes completar los datos del responsable para continuar.');
            return false;
        }

        // Validar firmas
        const firmaRef1Vacia = firmaRef?.current?.isEmpty?.() ?? true;

        if (firmaRef1Vacia) {
            mostrarErrorValidacion('Debes firmar el documento.');
            return false;
        }

        return true;
    };

    const validarPasoActual = () => {      
        
        // Paso legal
        if (activeStep === 0) {
            return validarPasoLegal();
        }

        // Paso general
        if (activeStep === 1) {
            return validarPasoGeneral();
        }

        // Paso de firma (último)
        if (activeStep === steps.length - 1) {
            return validarPasoFirma();
        }

        // Pasos de formulario específicos
        const pasoFormulario = activeStep - 2; 
        return validarPasoFormulario(pasoFormulario);
    };

    const persistirProgresoEnServidor = (overrides = {}) => {
        const { dispararOnSuccess = true, omitirDebounce = false, ...payloadOverrides } = overrides;
        const ahora = Date.now();
        if (!omitirDebounce) {
            if (guardadoEnCursoRef.current) return;
            if (ahora - ultimoGuardadoTsRef.current < 1200) return;
        }
        if (!props.bienestarId) {
            if (props.actualizarMensaje) {
                props.actualizarMensaje({
                    titulo: 'No hay solicitud asociada para guardar el progreso.',
                    tipo: 'warning',
                    mostrar: true,
                    tiempo: 3000,
                });
            }
            return;
        }
        guardadoEnCursoRef.current = true;
        setGuardandoProgreso(true);
        ultimoGuardadoTsRef.current = ahora;
        const body = {
            tipo_servicio_codigo: tipoFicha,
            codigo_ficha_secundario: codigoFichaSecundario,
            datos: serializarEstadoFicha(formState),
            paso_activo: activeStep,
            ...payloadOverrides,
        };
        postProgresoFichaSeguimiento(props.bienestarId, body).then(({ error, estado, resp }) => {
            guardadoEnCursoRef.current = false;
            setGuardandoProgreso(false);
            if (!error && estado === 200) {
                if (resp?.estado) {
                    setEstadoProgreso(resp.estado);
                }
                if (dispararOnSuccess && typeof props.onSuccess === 'function') {
                    props.onSuccess(resp);
                }
                if (props.actualizarMensaje && overrides.mostrarMensaje !== false) {
                    props.actualizarMensaje({
                        titulo: resp?.titulo || 'Progreso guardado',
                        tipo: 'success',
                        mostrar: true,
                        tiempo: 2500,
                    });
                }
            } else if (props.actualizarMensaje) {
                props.actualizarMensaje({
                    titulo: (resp && resp.error) || 'No se pudo guardar el progreso',
                    tipo: 'danger',
                    mostrar: true,
                    tiempo: 4000,
                });
            }
        });
    };

    const handleGuardarProgreso = () => {
        if (readOnly || !progresoListo) return;
        // Guardar progreso no debe forzar estado de negocio.
        // El backend decide si pasa a pending cuando se edita una ficha completed.
        persistirProgresoEnServidor();
    };

    const handleCerrarFicha = () => {
        if (readOnly || !props.puedeCerrarFicha) return;
        persistirProgresoEnServidor({ estado: 'closed' });
    };

    const ejecutarDescargaBorradorPdf = async () => {
        setConfirmDescargaBorradorOpen(false);
        if (readOnly || !progresoListo || !props.bienestarId || cargando || envioDocumentoEnCursoRef.current) return;
        envioDocumentoEnCursoRef.current = true;
        // Persistir primero los cambios para que backend actualice estado a pending
        // cuando la ficha estaba completed y fue editada.
        persistirProgresoEnServidor({ mostrarMensaje: false, dispararOnSuccess: false });
        setCargando(true);
        try {
            const resultado = await generarPdfConFirmas({
                data: formState,
                tipoFormulario: tipoFicha,
                codigoSecundario: props.fichaSeleccionada?.secundario?.codigo,
                tipoFormularioTitulo: props.fichaSeleccionada?.secundario?.nombre || 'Ficha de Seguimiento',
                firmaRef,
                firmaRefResponsable,
                requiereDobleFirma: false,
                bienestarId: props.bienestarId,
                solicitud: props.solicitud,
                isDraft: true,
                onExito: () => {
                    if (props.onSuccess) {
                        props.onSuccess();
                    }
                },
                onError: () => {},
                mostrarMensaje: (config) => {
                    if (props.actualizarMensaje) {
                        props.actualizarMensaje(config);
                    }
                },
            });
            if (!resultado && props.actualizarMensaje) {
                props.actualizarMensaje({
                    titulo: 'No se pudo generar el borrador',
                    tipo: 'warning',
                    mostrar: true,
                    tiempo: 4000,
                });
            }
        } finally {
            setCargando(false);
            envioDocumentoEnCursoRef.current = false;
        }
    };

    const handleDescargarBorrador = () => {
        if (readOnly || !progresoListo || !props.bienestarId || cargando || envioDocumentoEnCursoRef.current) return;
        const completa = isFichaSeguimientoCompleta({
            data: formState,
            tipoFicha,
            textoProteccionDatos: props.texto_proteccion_datos,
            firmaRef,
            firmaRefResponsable,
            requiereDobleFirma: false,
        });
        if (completa) {
            void ejecutarDescargaBorradorPdf();
        } else {
            setConfirmDescargaBorradorOpen(true);
        }
    };

    const handleEnviarConPDF = async () => {
        if (cargando || envioDocumentoEnCursoRef.current) return false;
        envioDocumentoEnCursoRef.current = true;
        // Validar antes de enviar
        if (!validarPasoActual()) {
            envioDocumentoEnCursoRef.current = false;
            return;
        }

        setCargando(true);
        
        try {
            const resultado = await generarPdfConFirmas({
                data: formState,
                tipoFormulario: tipoFicha,
                codigoSecundario: props.fichaSeleccionada?.secundario?.codigo,
                tipoFormularioTitulo: props.fichaSeleccionada?.secundario?.nombre || "Ficha de Seguimiento",
                firmaRef: firmaRef,
                firmaRefResponsable: firmaRefResponsable,
                requiereDobleFirma: false,
                bienestarId: props.bienestarId,
                solicitud: props.solicitud,
                isDraft: false,

                onExito: () => {
                    setEstadoProgreso('completed');
                    persistirProgresoEnServidor({ estado: 'completed', mostrarMensaje: false, dispararOnSuccess: false });
                    setCargando(false);

                    if (props.onSuccess) {
                        props.onSuccess();
                    }

                    if (props.mostrarModal) {
                        props.mostrarModal(false);
                    }
                    envioDocumentoEnCursoRef.current = false;
                },

                onError: (error) => {
                    setCargando(false);
                    envioDocumentoEnCursoRef.current = false;
                    
                    if (props.actualizarMensaje) {
                        props.actualizarMensaje({
                            titulo: error.message || 'Error al generar el documento',
                            tipo: "danger",
                            mostrar: true,
                            tiempo: 4000
                        });
                    }
                },

                mostrarMensaje: (config) => {
                    if (props.actualizarMensaje) {
                        props.actualizarMensaje(config);
                    }
                },
            });

            if (!resultado) {
                setCargando(false);
                envioDocumentoEnCursoRef.current = false;
                return false;
            }

            return true;

        } catch (error) {
            setCargando(false);
            envioDocumentoEnCursoRef.current = false;
            
            if (props.actualizarMensaje) {
                props.actualizarMensaje({
                    titulo: 'Error al procesar el documento',
                    tipo: "danger",
                    mostrar: true,
                    tiempo: 4000
                });
            }

            return false;
        }
    };

    return (
        <>
            <Dialog open={confirmDescargaBorradorOpen} onClose={() => setConfirmDescargaBorradorOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Descargar borrador</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Este documento se encuentra en estado borrador y no contiene firmas o está incompleto.
                        ¿Deseas descargarlo de todas formas?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDescargaBorradorOpen(false)} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={() => void ejecutarDescargaBorradorPdf()} variant="contained" color="primary" disabled={cargando || guardandoProgreso}>
                        Descargar
                    </Button>
                </DialogActions>
            </Dialog>
            {!progresoListo ? (
                <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TareasTerminadas
                        mensaje="Cargando ficha..."
                        marginTop="0"
                        imagen={emma_w}
                        widthImg="7%"
                        cargando={true}
                    />
                </div>
            ) : cargando ? (
                <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TareasTerminadas
                        mensaje="Generando documento..."
                        marginTop="0"
                        imagen={emma_w}
                        widthImg="7%"
                        cargando={true}
                    />
                </div>
            ) : (
                <div style={{ minHeight: '400px' }}>
                    <FormularioFichaSeguimiento 
                        {...props}
                        solicitud={solicitudEnriquecida || props.solicitud}
                        data={formState}
                        onChange={handleChange}
                        onBatchChange={handleBatchChange}
                        activeStep={activeStep}
                        firmaRefEntrevistado={firmaRef}
                        firmaRefResponsable={firmaRefResponsable}
                        onChangeSesion={handleChangeSesion}
                        formSteps={formSteps}
                        readOnly={readOnly}
                    />
                    <div
                        style={{
                            display: "flex",
                            flexDirection: accionesApiladas ? "column" : "row",
                            flexWrap: accionesApiladas ? "nowrap" : "wrap",
                            alignItems: accionesApiladas ? "stretch" : "center",
                            justifyContent: accionesApiladas ? "stretch" : "flex-end",
                            paddingTop: 20,
                            gap: 10,
                            width: "100%",
                            boxSizing: "border-box",
                        }}
                    >
                        <Button
                            disabled={activeStep === 0 || cargando || guardandoProgreso}
                            onClick={() => setActiveStep((prev) => prev - 1)}
                            variant="outlined"
                            color="primary"
                            fullWidth={accionesApiladas}
                        >
                            Atrás
                        </Button>
                        {!readOnly && (
                            <>
                                <Button variant="outlined" color="primary" onClick={handleGuardarProgreso} fullWidth={accionesApiladas}>
                                    Guardar progreso
                                </Button>
                                <Button variant="outlined" color="primary" onClick={handleDescargarBorrador} fullWidth={accionesApiladas} disabled={cargando || guardandoProgreso}>
                                    Descargar borrador
                                </Button>
                            </>
                        )}
                        {props.puedeCerrarFicha && !readOnly && estadoProgreso !== 'closed' && (
                            <Button variant="outlined" color="secondary" onClick={handleCerrarFicha} fullWidth={accionesApiladas} disabled={cargando || guardandoProgreso}>
                                Cerrar ficha
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={(readOnly && isLastStep) || cargando || guardandoProgreso}
                            fullWidth={accionesApiladas}
                            onClick={() => {
                                if (isLastStep) {
                                    handleEnviarConPDF();
                                } else if (readOnly) {
                                    setActiveStep((prev) => prev + 1);
                                } else if (validarPasoActual()) {
                                    setActiveStep((prev) => prev + 1);
                                }
                            }}
                        >
                            {isLastStep ? 'Guardar Seguimiento' : 'Siguiente'}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
}

/** Alias legado para imports existentes; mismo comportamiento que ConAcciones. */
export const FormularioFichaSeguimientoMaestro = FormularioFichaSeguimientoConAcciones;

